/**
 * `useCustomThemes` — React Query hook fronting the user-authored UI themes.
 *
 * 2026-06-01 — Chris ask: per-workspace user themes backed by Postgres
 * (multi-tenancy M2 RLS-scoped). Each device pulls the same list. Mutations
 * fire SSE "themes" family events so the other devices' UI auto-refreshes
 * via the existing useChangeEventStream invalidator.
 *
 * The TanStack Query key is ``['custom-themes']``; the SSE invalidator maps
 * the ``themes`` family to this same key (see useChangeEventStream).
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  createCustomTheme,
  deleteCustomTheme,
  listCustomThemes,
  updateCustomTheme,
  type CreateCustomThemeRequest,
  type UpdateCustomThemeRequest,
} from '@/api/endpoints';
import type { CustomTheme } from '@/types/wire';

export const CUSTOM_THEMES_KEY = ['custom-themes'] as const;

/** Fetch every custom theme in the caller's workspace. */
export function useCustomThemesQuery(): UseQueryResult<CustomTheme[]> {
  return useQuery({
    queryKey: CUSTOM_THEMES_KEY,
    queryFn: async () => (await listCustomThemes()).themes,
    staleTime: 60_000, // 1 minute — themes change rarely; SSE invalidates on writes
  });
}

/** Create a new custom theme. Invalidates the list on success. */
export function useCreateCustomThemeMutation(): UseMutationResult<
  CustomTheme,
  Error,
  CreateCustomThemeRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createCustomTheme,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CUSTOM_THEMES_KEY });
    },
  });
}

/** Update a custom theme's name and/or tokens. Invalidates on success. */
export function useUpdateCustomThemeMutation(): UseMutationResult<
  CustomTheme,
  Error,
  UpdateCustomThemeRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateCustomTheme,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CUSTOM_THEMES_KEY });
    },
  });
}

/** Hard-delete a custom theme. Invalidates on success. */
export function useDeleteCustomThemeMutation(): UseMutationResult<
  void,
  Error,
  string // themeId
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteCustomTheme,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CUSTOM_THEMES_KEY });
    },
  });
}
